import React from 'react';
import { TestTube, CheckCircle, AlertCircle, Eye, EyeOff, RefreshCw, DollarSign, TrendingDown } from 'lucide-react';
import { calculateTotalOpenAICost } from '../settingsHelpers';
import { OPENAI_USAGE_HISTORY_LIMIT } from '../constants';
import type { OpenAIConfig, TestResults, ShowPasswords } from '../types';

interface OpenAISettingsTabProps {
  config: OpenAIConfig;
  onChange: (config: OpenAIConfig) => void;
  testResults: TestResults;
  onTest: (service: string) => void;
  onRefreshBilling: () => void;
  showPasswords: ShowPasswords;
  onTogglePassword: (field: string) => void;
}

const OpenAISettingsTab: React.FC<OpenAISettingsTabProps> = ({
  config,
  onChange,
  testResults,
  onTest,
  onRefreshBilling,
  showPasswords,
  onTogglePassword
}) => {
  const totalCost = calculateTotalOpenAICost(config.usageHistory);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Configuración OpenAI</h3>
          <p className="text-sm text-gray-600">Para funciones de IA como resúmenes automáticos y sugerencias</p>
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">
              {config.enabled ? 'Habilitado' : 'Deshabilitado'}
            </span>
            <button
              type="button"
              onClick={() => onChange({ ...config, enabled: !config.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
          {testResults.openai !== undefined && (
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              testResults.openai ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {testResults.openai ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span className="text-sm">{testResults.openai ? 'Conectado' : 'Error'}</span>
            </div>
          )}
          <button
            onClick={() => onTest('openai')}
            disabled={!config.enabled}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TestTube className="w-4 h-4" />
            <span>Probar</span>
          </button>
        </div>
      </div>

      {testResults.openai && config.billingInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-blue-900">Información de Facturación</h4>
            <button
              onClick={onRefreshBilling}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualizar</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Crédito Total</p>
                  <p className="text-2xl font-bold text-blue-900">
                    ${config.billingInfo.totalCredit.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Crédito Usado</p>
                  <p className="text-2xl font-bold text-red-900">
                    ${config.billingInfo.usedCredit.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Crédito Disponible</p>
                  <p className="text-2xl font-bold text-green-900">
                    ${config.billingInfo.availableCredit.toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-blue-700">
            Última actualización: {new Date(config.billingInfo.lastUpdated).toLocaleString()}
          </div>
        </div>
      )}

      {testResults.openai && config.usageHistory.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Historial de Uso</h4>
              <div className="text-sm text-gray-600">
                Total gastado: <span className="font-bold text-red-600">
                  ${totalCost.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modelo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Costo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {config.usageHistory.slice(0, OPENAI_USAGE_HISTORY_LIMIT).map((usage, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{usage.action}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {usage.model}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="text-xs">
                        <div>Total: {usage.totalTokens}</div>
                        <div className="text-gray-500">
                          {usage.promptTokens}p + {usage.completionTokens}c
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-red-600">
                      ${usage.estimatedCost.toFixed(4)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(usage.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {config.usageHistory.length > OPENAI_USAGE_HISTORY_LIMIT && (
            <div className="p-4 text-center text-sm text-gray-500 border-t border-gray-200">
              Mostrando las últimas {OPENAI_USAGE_HISTORY_LIMIT} de {config.usageHistory.length} acciones
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key *</label>
          <div className="relative">
            <input
              type={showPasswords.openai ? 'text' : 'password'}
              value={config.apiKey}
              onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="sk-..."
            />
            <button
              type="button"
              onClick={() => onTogglePassword('openai')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organization ID (opcional)
          </label>
          <input
            type="text"
            value={config.organizationId}
            onChange={(e) => onChange({ ...config, organizationId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="org-..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
            <select
              value={config.model}
              onChange={(e) => onChange({ ...config, model: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
            <input
              type="number"
              min="100"
              max="4000"
              value={config.maxTokens}
              onChange={(e) => onChange({ ...config, maxTokens: parseInt(e.target.value) || 1000 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
            <input
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={config.temperature}
              onChange={(e) => onChange({ ...config, temperature: parseFloat(e.target.value) || 0.7 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">¿Cómo obtener tu API Key de OpenAI?</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Ve a <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com/api-keys</a></li>
          <li>Inicia sesión en tu cuenta de OpenAI</li>
          <li>Haz clic en "Create new secret key"</li>
          <li>Dale un nombre descriptivo (ej: "CRM Cero Ideas")</li>
          <li>Copia la clave que empieza con "sk-"</li>
        </ol>
        <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-300">
          <p className="text-xs text-blue-800">
            <strong>Funciones de IA habilitadas:</strong> Resúmenes automáticos de clientes,
            sugerencias de respuesta, análisis de leads, detección de duplicados y más.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OpenAISettingsTab;
